import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { collectChronologicalDates } from '../main-section.helpers';
import type { ChartDataItem } from './contributing-initiatives-chart.type';

/**
 * Hook that prepares data for the Contributing Initiatives chart
 *
 * Transforms the raw initiative data into a format suitable for the bar chart,
 * where each data point contains:
 * - date: The time period label
 * - [initiativeTitle]: The value for each selected initiative
 *
 * @returns {ChartDataItem[] | []} An array of data points for the chart, where each item
 * contains a date and values for each selected initiative
 */
const useChartData = (): ChartDataItem[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance) as any;
  const selectedInitiatives = useThroughputTabStore((state) => state.selectedInitiatives);
  const metric = useThroughputTabStore((state) => state.metric);

  const chartData = useMemo(() => {
    try {
      if (!initiative) {
        return [];
      }

      const dates = collectChronologicalDates(initiative.contributing_initiatives_over_time, metric);

      const data = dates.map((date) => {
        const dataEntries = selectedInitiatives.map((initiative) => {
          const total = Object.values(initiative.contributing_teams_over_time ?? {}).reduce((sum, value) => {
            const metricValues = value?.[metric];
            if (!metricValues) {
              return sum;
            }

            return sum + (metricValues[date] ?? 0);
          }, 0);

          return [initiative.id, total];
        });

        return {
          date,
          ...Object.fromEntries(dataEntries),
        };
      });

      return data;
    } catch (error) {
      Sentry.captureException(error);
      return [];
    }
  }, [initiative, selectedInitiatives, metric]);

  return chartData;
};

export { useChartData };
